/**
 * Query Builder - Utilities for constructing and serializing queries
 *
 * Provides:
 * - Converting UI options to queries
 * - Parsing text expressions to queries
 * - Serializing queries to text
 * - Preset query templates
 */

import type {
  TaskQuery,
  QueryNode,
  QueryCondition,
  QueryOperator,
  FilterableField,
} from '../types/query';
import { createQuery, and, or, q, isQueryGroup, QUERY_SCHEMA_VERSION } from '../types/query';

// ============================================================================
// Build queries from UI options (widget config)
// ============================================================================

export interface UIFilterOptions {
  statusFilter?: string[];
  horizonFilter?: string[];
  tagsFilter?: string[];
  assigneeFilter?: string | null;
  dateFilter?: 'all' | 'today' | 'tomorrow' | 'this-week' | 'this-month' | 'overdue' | 'no-date' | 'has-date';
  dateFilters?: ('today' | 'tomorrow' | 'this-week' | 'this-month' | 'overdue' | 'no-date' | 'has-date')[];
  showCompleted?: boolean;
  onlyWithDeadline?: boolean;
  onlyOverdue?: boolean;
  parentOnly?: boolean;
  childOnly?: boolean;
  searchQuery?: string;
}

/**
 * Build a TaskQuery from UI filter options
 * This is the bridge between checkbox/dropdown UI and the query system
 */
export function buildQueryFromUI(options: UIFilterOptions): TaskQuery {
  const conditions: QueryNode[] = [];

  // Status filter
  if (options.statusFilter && options.statusFilter.length > 0) {
    conditions.push(q.in('status', options.statusFilter));
  }

  // Hide completed and canceled (if showCompleted is explicitly false)
  if (options.showCompleted === false) {
    conditions.push(q.neq('status', 'completed'));
    conditions.push(q.neq('status', 'canceled'));
  }

  // Horizon filter
  if (options.horizonFilter && options.horizonFilter.length > 0) {
    conditions.push(q.in('horizon', options.horizonFilter));
  }

  // Tags filter (has any of the selected tags)
  if (options.tagsFilter && options.tagsFilter.length > 0) {
    conditions.push(q.hasAny('tags', options.tagsFilter));
  }

  // Assignee filter
  if (options.assigneeFilter) {
    conditions.push(q.eq('assigned_to_id', options.assigneeFilter));
  }

  // Date filter (multi-select takes precedence over single)
  if (options.dateFilters && options.dateFilters.length > 0) {
    const dateConditions = options.dateFilters
      .map(f => buildDateCondition(f))
      .filter((c): c is QueryNode => c !== null);
    if (dateConditions.length === 1) {
      conditions.push(dateConditions[0]);
    } else if (dateConditions.length > 1) {
      conditions.push(or(...dateConditions));
    }
  } else if (options.dateFilter && options.dateFilter !== 'all') {
    const dateCondition = buildDateCondition(options.dateFilter);
    if (dateCondition) {
      conditions.push(dateCondition);
    }
  }

  // Only with deadline
  if (options.onlyWithDeadline) {
    conditions.push(q.isNotNull('deadline'));
  }

  // Only overdue
  if (options.onlyOverdue) {
    conditions.push(
      and(
        q.lt('deadline', '$today'),
        q.neq('status', 'completed')
      )
    );
  }

  // Parent only
  if (options.parentOnly) {
    conditions.push(q.isNull('parent_id'));
  }

  // Child only
  if (options.childOnly) {
    conditions.push(q.isNotNull('parent_id'));
  }

  // Search query (searches title and description)
  if (options.searchQuery && options.searchQuery.trim()) {
    conditions.push(
      or(
        q.contains('title', options.searchQuery.trim()),
        q.contains('description', options.searchQuery.trim())
      )
    );
  }

  // Build final query
  if (conditions.length === 0) {
    return createQuery(null);
  }
  if (conditions.length === 1) {
    return createQuery(conditions[0]);
  }
  return createQuery(and(...conditions));
}

function buildDateCondition(dateFilter: UIFilterOptions['dateFilter']): QueryNode | null {
  switch (dateFilter) {
    case 'today':
      return and(q.gte('deadline', '$today'), q.lt('deadline', '$tomorrow'));

    case 'tomorrow':
      return q.eq('deadline', '$tomorrow');

    case 'this-week':
      return and(q.gte('deadline', '$startOfWeek'), q.lte('deadline', '$endOfWeek'));

    case 'this-month':
      return and(q.gte('deadline', '$startOfMonth'), q.lte('deadline', '$endOfMonth'));

    case 'overdue':
      return and(
        q.lt('deadline', '$today'),
        q.neq('status', 'completed')
      );

    case 'no-date':
      return q.isNull('deadline');

    case 'has-date':
      return q.isNotNull('deadline');

    default:
      return null;
  }
}

// ============================================================================
// Query to text expression
// ============================================================================

const OPERATOR_SYMBOLS: Record<QueryOperator, string> = {
  eq: '=',
  neq: '!=',
  gt: '>',
  gte: '>=',
  lt: '<',
  lte: '<=',
  in: 'IN',
  notIn: 'NOT IN',
  hasAny: 'HAS ANY',
  hasAll: 'HAS ALL',
  isEmpty: 'IS EMPTY',
  isNotEmpty: 'IS NOT EMPTY',
  contains: 'CONTAINS',
  startsWith: 'STARTS WITH',
  endsWith: 'ENDS WITH',
  isNull: 'IS NULL',
  isNotNull: 'IS NOT NULL',
};

/**
 * Convert a query to a human-readable text expression
 */
export function queryToText(query: TaskQuery | null): string {
  if (!query || !query.root) return '';
  return nodeToText(query.root);
}

function nodeToText(node: QueryNode): string {
  if (isQueryGroup(node)) {
    if (node.conditions.length === 0) return '';
    if (node.conditions.length === 1) return nodeToText(node.conditions[0]);

    const parts = node.conditions.map(child => {
      const text = nodeToText(child);
      // Wrap nested groups in parentheses if they have different logic
      if (isQueryGroup(child) && child.logic !== node.logic) {
        return `(${text})`;
      }
      return text;
    });

    const separator = node.logic === 'and' ? ' AND ' : ' OR ';
    return parts.join(separator);
  }

  // Single condition
  const { field, operator, value } = node;
  const opSymbol = OPERATOR_SYMBOLS[operator] || operator;

  // Operators without values
  if (['isNull', 'isNotNull', 'isEmpty', 'isNotEmpty'].includes(operator)) {
    return `${field} ${opSymbol}`;
  }

  // Format value
  let valueStr: string;
  if (Array.isArray(value)) {
    valueStr = `(${value.map(v => formatValue(v)).join(', ')})`;
  } else {
    valueStr = formatValue(value);
  }

  return `${field} ${opSymbol} ${valueStr}`;
}

function formatValue(value: unknown): string {
  if (typeof value === 'string') {
    // Keep date expressions as-is
    if (value.startsWith('$')) return value;
    // Quote strings with spaces
    if (value.includes(' ')) return `"${value}"`;
    return value;
  }
  return String(value);
}

// ============================================================================
// Text expression to query (parser)
// ============================================================================

/**
 * Parse a text expression into a TaskQuery
 *
 * Supported syntax:
 *   field = value
 *   field != value
 *   field > value
 *   field >= value
 *   field < value
 *   field <= value
 *   field IN (a, b, c)
 *   field NOT IN (a, b, c)
 *   field HAS ANY (a, b, c)
 *   field HAS ALL (a, b, c)
 *   field CONTAINS value
 *   field IS NULL
 *   field IS NOT NULL
 *   condition AND condition
 *   condition OR condition
 *   (grouped conditions)
 */
export function parseQueryText(text: string): TaskQuery | null {
  const trimmed = text.trim();
  if (!trimmed) return null;

  try {
    const root = parseExpression(trimmed);
    return root ? { version: QUERY_SCHEMA_VERSION, root } : null;
  } catch (e) {
    console.error('Query parse error:', e);
    return null;
  }
}

function parseExpression(text: string): QueryNode | null {
  text = text.trim();
  if (!text) return null;

  // Handle parentheses at the start
  if (text.startsWith('(')) {
    const closeIndex = findMatchingParen(text, 0);
    if (closeIndex === text.length - 1) {
      // Entire expression is wrapped in parens
      return parseExpression(text.slice(1, -1));
    }
  }

  // Split by AND (case insensitive), respecting parentheses
  const andParts = splitByOperator(text, ' AND ');
  if (andParts.length > 1) {
    const conditions = andParts
      .map(part => parseExpression(part))
      .filter((c): c is QueryNode => c !== null);
    return conditions.length > 0 ? { logic: 'and', conditions } : null;
  }

  // Split by OR
  const orParts = splitByOperator(text, ' OR ');
  if (orParts.length > 1) {
    const conditions = orParts
      .map(part => parseExpression(part))
      .filter((c): c is QueryNode => c !== null);
    return conditions.length > 0 ? { logic: 'or', conditions } : null;
  }

  // Parse single condition
  return parseCondition(text);
}

function parseCondition(text: string): QueryCondition | null {
  text = text.trim();

  // IS NULL / IS NOT NULL
  const isNullMatch = text.match(/^(\w+)\s+IS\s+NULL$/i);
  if (isNullMatch) {
    return { field: isNullMatch[1] as FilterableField, operator: 'isNull' };
  }

  const isNotNullMatch = text.match(/^(\w+)\s+IS\s+NOT\s+NULL$/i);
  if (isNotNullMatch) {
    return { field: isNotNullMatch[1] as FilterableField, operator: 'isNotNull' };
  }

  // IS EMPTY / IS NOT EMPTY
  const isEmptyMatch = text.match(/^(\w+)\s+IS\s+EMPTY$/i);
  if (isEmptyMatch) {
    return { field: isEmptyMatch[1] as FilterableField, operator: 'isEmpty' };
  }

  const isNotEmptyMatch = text.match(/^(\w+)\s+IS\s+NOT\s+EMPTY$/i);
  if (isNotEmptyMatch) {
    return { field: isNotEmptyMatch[1] as FilterableField, operator: 'isNotEmpty' };
  }

  // IN / NOT IN / HAS ANY / HAS ALL with array values
  const arrayMatch = text.match(/^(\w+)\s+(IN|NOT\s+IN|HAS\s+ANY|HAS\s+ALL)\s+\(([^)]+)\)$/i);
  if (arrayMatch) {
    const [, field, op, valuesStr] = arrayMatch;
    const values = valuesStr.split(',').map(v => parseValue(v.trim()));
    const operatorMap: Record<string, QueryOperator> = {
      'in': 'in',
      'not in': 'notIn',
      'has any': 'hasAny',
      'has all': 'hasAll',
    };
    return {
      field: field as FilterableField,
      operator: operatorMap[op.toLowerCase().replace(/\s+/g, ' ')] || 'in',
      value: values,
    };
  }

  // CONTAINS / STARTS WITH / ENDS WITH
  const stringOpMatch = text.match(/^(\w+)\s+(CONTAINS|STARTS\s+WITH|ENDS\s+WITH)\s+(.+)$/i);
  if (stringOpMatch) {
    const [, field, op, valueStr] = stringOpMatch;
    const operatorMap: Record<string, QueryOperator> = {
      'contains': 'contains',
      'starts with': 'startsWith',
      'ends with': 'endsWith',
    };
    return {
      field: field as FilterableField,
      operator: operatorMap[op.toLowerCase().replace(/\s+/g, ' ')] || 'contains',
      value: parseValue(valueStr.trim()),
    };
  }

  // Comparison operators: =, !=, <>, >, >=, <, <=
  const compMatch = text.match(/^(\w+)\s*(=|!=|<>|>=|<=|>|<)\s*(.+)$/);
  if (compMatch) {
    const [, field, op, valueStr] = compMatch;
    const operatorMap: Record<string, QueryOperator> = {
      '=': 'eq',
      '!=': 'neq',
      '<>': 'neq',
      '>': 'gt',
      '>=': 'gte',
      '<': 'lt',
      '<=': 'lte',
    };
    return {
      field: field as FilterableField,
      operator: operatorMap[op] || 'eq',
      value: parseValue(valueStr.trim()),
    };
  }

  console.warn(`Could not parse condition: ${text}`);
  return null;
}

function parseValue(text: string): unknown {
  // Remove quotes
  if ((text.startsWith('"') && text.endsWith('"')) ||
      (text.startsWith("'") && text.endsWith("'"))) {
    return text.slice(1, -1);
  }

  // Booleans
  if (text.toLowerCase() === 'true') return true;
  if (text.toLowerCase() === 'false') return false;

  // Numbers
  if (/^-?\d+$/.test(text)) return parseInt(text, 10);
  if (/^-?\d+\.\d+$/.test(text)) return parseFloat(text);

  // Date expressions (keep as string)
  if (text.startsWith('$')) return text;

  // Plain string
  return text;
}

function findMatchingParen(text: string, start: number): number {
  let depth = 0;
  for (let i = start; i < text.length; i++) {
    if (text[i] === '(') depth++;
    else if (text[i] === ')') {
      depth--;
      if (depth === 0) return i;
    }
  }
  return -1;
}

function splitByOperator(text: string, operator: string): string[] {
  const parts: string[] = [];
  let depth = 0;
  let current = '';
  let i = 0;

  while (i < text.length) {
    if (text[i] === '(') {
      depth++;
      current += text[i];
      i++;
    } else if (text[i] === ')') {
      depth--;
      current += text[i];
      i++;
    } else if (depth === 0 && text.slice(i).toUpperCase().startsWith(operator.toUpperCase())) {
      if (current.trim()) parts.push(current.trim());
      current = '';
      i += operator.length;
    } else {
      current += text[i];
      i++;
    }
  }

  if (current.trim()) parts.push(current.trim());
  return parts;
}

// ============================================================================
// Preset Queries
// ============================================================================

export const QUERY_PRESETS: Record<string, { name: string; description: string; query: TaskQuery }> = {
  'todays-tasks': {
    name: "Today's Tasks",
    description: 'Tasks due today',
    query: createQuery(q.eq('deadline', '$today')),
  },

  'this-week': {
    name: 'This Week',
    description: 'Tasks due this week',
    query: createQuery(
      and(
        q.neq('status', 'completed'),
        q.gte('deadline', '$startOfWeek'),
        q.lte('deadline', '$endOfWeek'),
      )
    ),
  },

  'overdue': {
    name: 'Overdue',
    description: 'Tasks past their deadline',
    query: createQuery(
      and(
        q.lt('deadline', '$today'),
        q.neq('status', 'completed')
      )
    ),
  },

  'now-tasks': {
    name: 'Now',
    description: 'Tasks on the Now horizon',
    query: createQuery(
      and(
        q.eq('horizon', 'now'),
        q.neq('status', 'completed')
      )
    ),
  },

  'due-soon': {
    name: 'Due Soon',
    description: 'Tasks due in the next 3 days',
    query: createQuery(
      and(
        q.lte('deadline', '$daysFromNow(3)'),
        q.gte('deadline', '$today'),
        q.neq('status', 'completed')
      )
    ),
  },

  'no-date': {
    name: 'Unscheduled',
    description: 'Tasks without a deadline',
    query: createQuery(
      and(
        q.isNull('deadline'),
        q.neq('status', 'completed')
      )
    ),
  },

  'in-progress': {
    name: 'In Progress',
    description: 'Tasks currently being worked on',
    query: createQuery(q.eq('status', 'in_progress')),
  },

  'waiting': {
    name: 'Waiting',
    description: 'Tasks waiting on something',
    query: createQuery(q.eq('status', 'waiting')),
  },

  'completed-today': {
    name: 'Completed Today',
    description: 'Tasks completed today',
    query: createQuery(
      and(
        q.eq('status', 'completed'),
        q.eq('completed_date', '$today')
      )
    ),
  },

  'all-active': {
    name: 'All Active',
    description: 'All non-completed, non-canceled tasks',
    query: createQuery(
      and(
        q.neq('status', 'completed'),
        q.neq('status', 'canceled')
      )
    ),
  },
};
